// The "Refined" design option's switch (design/pms-ui-refined, spec section 0).
//
//   ?ui=refined      sets data-ui="refined" on <html> and remembers it in
//                    localStorage["pms.ui"].
//   ?ui=off          (alias ?ui=classic) classic UI, remembered as "off": the
//                    per-browser rollback.
//   ?ui=default      forgets the stored choice; the deployment default applies.
//
// Refined is the default. Precedence: query > localStorage > the deployment's
// default (`ui` in the injected brand, from PMS_UI) > refined.
//   ?density=compact sets data-density="compact" and remembers it in
//                    localStorage["pms.density"]; ?density=standard forgets it.
//                    Compact only does anything together with refined.
//
// Classic is the absence of the attribute: every refined rule is scoped under
// `[data-ui='refined']` in styles/refined.scss.
//
// The flags are read ONCE, before the first render (`applyBrand` calls
// `applyUiFlags`), so they are plain document attributes afterwards. Components
// that must render different markup read them through `useUiFlag()`; code
// outside React uses `isRefined()`.
import { useSyncExternalStore } from 'react'

export type UiMode = 'refined' | null
export type Density = 'compact' | null
/** What a deployment (PMS_UI) says the default interface is. */
export type DeployUi = 'refined' | 'classic'

export const UI_KEY = 'pms.ui'
export const DENSITY_KEY = 'pms.density'

/** The slice of localStorage this module needs. Storage can be absent or throw. */
export interface FlagStore {
  getItem(k: string): string | null
  setItem(k: string, v: string): void
  removeItem(k: string): void
}

export interface UiFlags {
  ui: UiMode
  density: Density
}

function safe<T>(f: () => T, otherwise: T): T {
  try {
    return f()
  } catch {
    return otherwise
  }
}

/**
 * Work out the flags from a query string and the stored choices, and persist a
 * choice made in the query. A query value wins; an unknown query value is
 * ignored (it neither changes nor clears what is stored); a stored value that is
 * not recognised counts as unset, and unset falls to `deployment`.
 */
export function resolveUiFlags(
  search: string,
  store: FlagStore | null,
  deployment: DeployUi = 'refined',
): UiFlags {
  const q = new URLSearchParams(search)
  const stored = (k: string) => (store ? safe(() => store.getItem(k), null) : null)
  const put = (k: string, v: string | null) => {
    if (!store) return
    safe(() => (v === null ? store.removeItem(k) : store.setItem(k, v)), undefined)
  }

  const dflt: UiMode = deployment === 'classic' ? null : 'refined'
  const st = stored(UI_KEY)
  let ui: UiMode = st === 'refined' ? 'refined' : st === 'off' || st === 'classic' ? null : dflt
  const qUi = q.get('ui')
  if (qUi === 'refined') {
    ui = 'refined'
    put(UI_KEY, 'refined')
  } else if (qUi === 'off' || qUi === 'classic') {
    ui = null
    put(UI_KEY, 'off')
  } else if (qUi === 'default') {
    ui = dflt
    put(UI_KEY, null)
  }

  let density: Density = stored(DENSITY_KEY) === 'compact' ? 'compact' : null
  const qDensity = q.get('density')
  if (qDensity === 'compact') {
    density = 'compact'
    put(DENSITY_KEY, 'compact')
  } else if (qDensity === 'standard' || qDensity === 'off') {
    density = null
    put(DENSITY_KEY, null)
  }

  return { ui, density }
}

/** Write the flags onto <html>. Absent means off, never an empty attribute. */
export function setUiAttributes(flags: UiFlags, doc: Document = document): void {
  const el = doc.documentElement
  if (flags.ui) el.setAttribute('data-ui', flags.ui)
  else el.removeAttribute('data-ui')
  if (flags.density) el.setAttribute('data-density', flags.density)
  else el.removeAttribute('data-density')
}

/** Read the query string and storage of `doc`'s window and apply the result. */
export function applyUiFlags(doc: Document = document, deployment: DeployUi = 'refined'): UiFlags {
  const win = doc.defaultView
  if (!win) return { ui: null, density: null }
  const store = safe<FlagStore | null>(() => win.localStorage, null)
  const flags = resolveUiFlags(win.location.search, store, deployment)
  setUiAttributes(flags, doc)
  return flags
}

/** What is on <html> now. */
export function currentUiFlags(doc?: Document): UiFlags {
  const d = doc ?? (typeof document === 'undefined' ? null : document)
  if (!d) return { ui: null, density: null }
  const el = d.documentElement
  return {
    ui: el.getAttribute('data-ui') === 'refined' ? 'refined' : null,
    density: el.getAttribute('data-density') === 'compact' ? 'compact' : null,
  }
}

export function isRefined(doc?: Document): boolean {
  return currentUiFlags(doc).ui === 'refined'
}

// The attributes are fixed for the life of the page, so there is nothing to
// subscribe to. A stable snapshot string keeps useSyncExternalStore happy.
const noSubscribe = () => () => {}
const snapshot = () => {
  const f = currentUiFlags()
  return `${f.ui ?? ''}|${f.density ?? ''}`
}

export interface UiFlagState {
  /** True when the Refined option is on. */
  refined: boolean
  /** True when Refined is on AND compact density was asked for. */
  compact: boolean
}

/** For components that render different markup under the option. */
export function useUiFlag(): UiFlagState {
  const s = useSyncExternalStore(noSubscribe, snapshot, () => '|')
  const [ui, density] = s.split('|')
  return { refined: ui === 'refined', compact: ui === 'refined' && density === 'compact' }
}
